<template>
  <div class="flex flex-col">
    <label :for="field.id" class="custom-file-upload">
      <FileUploadIcon></FileUploadIcon>
      <span
        v-if="!isActive"
        class="file-upload-content"
        :data-placeholder="t('common.fileUploadPlaceholder')"
      >
        {{
          placeholder
            ? `${placeholder} ${picklistOptions.length ? "-" : ""} ${picklistOptions.join(", ")}`
            : ""
        }}
      </span>
      <label
        v-else
        contenteditable="true"
        :data-placeholder="t('common.fileUploadPlaceholder')"
        class="form-placeholder cursor-text text-center shadow-none focus:outline-transparent focus:ring-0"
        @input="handleInput"
        @keydown="handleSafariCursor"
        @focus="handleFocus"
        @blur="handleBlur"
        v-text="inputPlaceholder"
      ></label>
    </label>
    <input :id="field.id" class="file-input" type="file" disabled />
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @input="
          (e) => {
            shortLabel = (e.target as HTMLElement).textContent;
            updateShortLabel();
            handleSafariCursor(e);
          }
        "
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="field.shortLabel" class="short-label font-inter break-words text-sm">{{
        field.shortLabel
      }}</span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { fixSafariCursor } from "@/util/methods";
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import FileUploadIcon from "./../../assets/icons/file-upload.svg";

const { t } = useI18n();

const props = defineProps({
  field: {
    type: Object,
    default() {
      return {};
    },
  },
  placeholder: { type: String, default: "" },
  picklistOptions: {
    type: Array,
    default() {
      return [];
    },
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:placeholder", "update:shortLabel"]);
const inputPlaceholder = ref(props.placeholder);
const shortLabel = ref(props.field.shortLabel || "");
const shortLabelElement = ref<HTMLElement | null>(null);
const store = useAppStore();

// Handler method to call the fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

watch(
  () => props.placeholder,
  (newVal) => {
    inputPlaceholder.value = newVal;
  }
);

watch(
  () => props.field.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

const handleInput = (event: Event) => {
  const target = event.target as HTMLLabelElement;
  inputPlaceholder.value = target.innerText;
  emit("update:placeholder", inputPlaceholder.value);
  // Keep cursor position consistent across browsers (especially Safari)
  handleSafariCursor(event);
};

const handleFocus = () => {
  // Ensure the label has the placeholder value when focused
  if (!inputPlaceholder.value) {
    inputPlaceholder.value = props.placeholder;
  }
};

const handleBlur = () => {
  store.addToHistory();
};

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);
</script>

<style scoped>
.file-input {
  display: none;
}

.custom-file-upload {
  border: 1px solid #ccc;
  border-radius: 4px;
  display: block !important;
  width: 100%;
  min-height: 5rem;
  text-align: center;
}

.custom-file-upload svg {
  width: 30px;
  display: block;
  margin: 5px auto;
}

#_builder-form .custom-file-upload {
  color: #6e7d84 !important;
}

.bi-upload {
  font-size: 20px;
}

.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}

[contenteditable="true"] {
  min-height: 24px;
  display: inline-block;
  width: 100%;
  outline: none;
  background-color: transparent;
  border: none;
}

.file-upload-content:empty:before {
  content: attr(data-placeholder);
  color: #aaa;
  pointer-events: none;
}

[contenteditable="true"]:empty:before {
  content: attr(data-placeholder);
  color: #aaa;
  pointer-events: none;
}
</style>
